import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { nullishStringToNull } from '~/utils/string';
import type { HashValue } from '~/utils/tags';

export const ARRAY_SEPARATOR = '|';

export function useUrlParams() {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const hashParams = useMemo(() => new URLSearchParams(location.hash.slice(1)), [location]);

  function getAllSearchParamsByKey(key: string) {
    const params = searchParams.get(key);
    return params;
  }

  const getHashValueByKey = useCallback(
    <HashValue extends string>(key: string) => {
      const hash = location.hash.slice(1);
      const hashParams = new URLSearchParams(hash);
      const params = hashParams.get(key) as HashValue | undefined;

      return params ?? undefined;
    },
    [location],
  );

  // Used when each filter value is a string
  const getAllHashValuesByKey = useCallback(
    (key: string) => {
      const hash = location.hash.slice(1);
      const hashParams = new URLSearchParams(hash);
      const params = hashParams.getAll(key);

      return params.length ? params.map(nullishStringToNull) : undefined;
    },
    [location],
  );

  // Used when each filter value is an array
  const getAllHashArrayValuesByKey = useCallback(
    (key: string) => {
      const hash = location.hash.slice(1);
      const hashParams = new URLSearchParams(hash);
      const params = hashParams.getAll(key);

      return params.length
        ? params.map((p) => {
            const value = nullishStringToNull(p);
            return value === null ? value : value.split(ARRAY_SEPARATOR);
          })
        : undefined;
    },
    [location],
  );

  const getAllHashKeys = useCallback(() => {
    const hash = location.hash.slice(1);
    const hashParams = new URLSearchParams(hash);

    return Array.from(hashParams.keys());
  }, [location]);

  const hashValuesToArray = useCallback(() => {
    const hash = location.hash.slice(1);
    const hashParams = new URLSearchParams(hash);

    const hashValues: Array<HashValue> = [];

    hashParams.forEach((value, key) => {
      const existingEntry = hashValues.find((entry) => entry[key]);

      if (existingEntry) {
        existingEntry[key].push(value);
      } else {
        hashValues.push({ [key]: [value] });
      }
    });

    return hashValues.length ? hashValues : undefined;
  }, [location]);

  const setUrlParams = useCallback(
    (params = searchParams, hash = hashParams, replaceURL = false) => {
      const sanitizedParams = params ? params.toString() : searchParams.toString();
      const sanitizedHash = hash ? hash.toString() : undefined;

      navigate(
        {
          hash: sanitizedHash,
          search: sanitizedParams,
        },
        { replace: replaceURL },
      );
    },
    [hashParams, searchParams, navigate],
  );

  const deleteHashKey = useCallback(
    (key: string) => {
      const locationHash = new URLSearchParams(location.hash.slice(1));

      locationHash.delete(key);
      setUrlParams(searchParams, locationHash);
    },
    [location, searchParams, setUrlParams],
  );

  const deleteHashKeys = useCallback(
    (keys: Array<string>) => {
      const locationHash = new URLSearchParams(location.hash.slice(1));

      for (const key of keys) {
        locationHash.delete(key);
      }

      setUrlParams(searchParams, locationHash);
    },
    [location, searchParams, setUrlParams],
  );

  return {
    deleteHashKey,
    deleteHashKeys,
    getAllHashArrayValuesByKey,
    getAllHashKeys,
    getAllHashValuesByKey,
    getAllSearchParamsByKey,
    getHashValueByKey,
    hashParams,
    hashValuesToArray,
    location,
    searchParams,
    setSearchParams,
    setUrlParams,
  };
}

export type GetAllHashValuesByKey = ReturnType<typeof useUrlParams>['getAllHashValuesByKey'];
export type GetAllHashArrayValuesByKey = ReturnType<typeof useUrlParams>['getAllHashArrayValuesByKey'];
export type GetAllHashKeys = ReturnType<typeof useUrlParams>['getAllHashKeys'];
export type GetHashValueByKey = ReturnType<typeof useUrlParams>['getHashValueByKey'];
export type HashValuesToArray = ReturnType<typeof useUrlParams>['hashValuesToArray'];
